import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { calculateAge, isTobacco, zipCodeToState } from '@shared';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import {
  selectClient,
  selectMaybeClientPartner,
  accumulatePaymentOptionQuotes,
  clearAllPaymentOptionQuotes,
  setIsLoadingAllPaymentOptions,
} from '@/model';
import { quotesApi } from '@/api/waterlily';
import type { ShopInsuranceMode } from '@/features/shop-insurance';
import type { Quote } from '@/model/quotesModel';

interface UseAllPaymentOptionQuotesFetchingProps {
  mode: ShopInsuranceMode;
  selectedTab: ShopInsuranceTab;
  hasPartnerWithDemographics: boolean;
}

export function useAllPaymentOptionQuotesFetching({
  mode,
  selectedTab,
  hasPartnerWithDemographics,
}: UseAllPaymentOptionQuotesFetchingProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const { flags } = useFeatureFlags();
  const fetchingRef = useRef(false);

  useEffect(() => {
    fetchingRef.current = false;
  }, [mode, selectedTab]);

  useEffect(() => {
    if (mode !== 'benefit-design' || !client?.intakeSurvey) return;
    if (fetchingRef.current) return;

    fetchingRef.current = true;
    dispatch(clearAllPaymentOptionQuotes());
    dispatch(setIsLoadingAllPaymentOptions(true));

    const age = calculateAge(new Date(client.intakeSurvey.clientBirthDate));
    const clientTobaccoStatus = isTobacco(client.intakeSurvey) ? 'Tobacco' : 'Non-Tobacco';
    const gender = client.intakeSurvey.clientGenderAtBirth === 'Male' ? 'Male' : 'Female';
    const state = zipCodeToState(client.intakeSurvey.clientZipCode);
    const usaState =
      flags.nycLifeDemo && state.stateAbbreviation === 'NY' ? 'NJ' : state.stateAbbreviation;

    const policyTypeMapping: Record<ShopInsuranceTab, string> = {
      single: 'Single Life',
      joint: 'Joint Life',
      couples: 'Couples Discount',
    };

    // Omitting cobPaymentOption causes the server to return ALL pay options in one call
    const demographics: FetchQuotesDemographics = {
      primaryAge: age,
      primaryGender: gender,
      primaryTobaccoStatus: clientTobaccoStatus,
      usaState,
      policyType: policyTypeMapping[selectedTab],
      maritalStatus: client.intakeSurvey.clientMaritalStatus,
      predictedCareStartAge: client.appliedInferenceSet?.ltcAtAge,
    };

    if (selectedTab === 'joint' && hasPartnerWithDemographics && clientPartner?.intakeSurvey) {
      const partnerAge = calculateAge(new Date(clientPartner.intakeSurvey.clientBirthDate));
      demographics.secondaryAge = partnerAge;
      demographics.secondaryGender =
        clientPartner.intakeSurvey.clientGenderAtBirth === 'Male' ? 'Male' : 'Female';
      demographics.secondaryTobaccoStatus = isTobacco(clientPartner.intakeSurvey)
        ? 'Tobacco'
        : 'Non-Tobacco';
    }

    const fetchAll = async () => {
      try {
        const result = await quotesApi.query({ demographics, advisorId: client.advisorId });
        if (result?.quotes?.length) {
          dispatch(accumulatePaymentOptionQuotes(result.quotes as Quote[]));
        }
      } catch {
        // non-fatal
      } finally {
        fetchingRef.current = false;
        dispatch(setIsLoadingAllPaymentOptions(false));
      }
    };

    fetchAll();
  }, [mode, selectedTab, client, clientPartner, hasPartnerWithDemographics, flags.nycLifeDemo]);

  return {};
}
